import React, { useState } from "react";
import { Link, NavLink, useNavigate, Outlet, useLocation } from "react-router-dom";
import { FexidocMark, FexidocLogo } from "./FexidocLogo";
import VerifyGate from "./VerifyGate";
import { LayoutDashboard, FilePlus2, FolderOpen, FileStack, ScrollText, Upload, Activity, Users, Settings, CreditCard, LogOut, ChevronDown, PanelLeftClose, PanelLeft, Menu, X } from "lucide-react";
import { useAuth } from "../lib/auth";

const nav = [
  { to: "/app", icon: LayoutDashboard, label: "Dashboard", end: true, testid: "nav-dashboard" },
  { to: "/app/new", icon: FilePlus2, label: "Create Document", testid: "nav-create" },
  { to: "/app/documents", icon: FolderOpen, label: "Documents", testid: "nav-documents" },
  { to: "/app/templates", icon: FileStack, label: "Templates", testid: "nav-templates" },
  { to: "/app/standards", icon: ScrollText, label: "Standards", testid: "nav-standards" },
  { to: "/app/uploads", icon: Upload, label: "Uploads", testid: "nav-uploads" },
  { to: "/app/activity", icon: Activity, label: "Activity", testid: "nav-activity" },
];

const bottomNav = [
  { to: "/app/team", icon: Users, label: "Team", testid: "nav-team" },
  { to: "/app/settings", icon: Settings, label: "Settings", testid: "nav-settings" },
  { to: "/app/billing", icon: CreditCard, label: "Billing", testid: "nav-billing" },
];

export default function AppShell() {
  const { user, activeWorkspace, workspaces, selectWorkspace, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [wsOpen, setWsOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  const doLogout = () => { logout(); navigate("/login"); };

  const width = collapsed ? "lg:w-[64px]" : "lg:w-[248px]";
  return (
    <div className="min-h-screen flex bg-[#050816] text-slate-100">
      {drawerOpen && (
        <button
          onClick={() => setDrawerOpen(false)}
          className="fixed inset-0 bg-black/70 z-30 lg:hidden"
          aria-label="Close navigation"
          data-testid="sidebar-overlay"
        />
      )}
      <aside className={`${width} w-[248px] shrink-0 border-r border-[#1E293B] bg-[#070B18] flex flex-col transition-transform lg:transition-all fixed inset-y-0 left-0 z-40 lg:static lg:translate-x-0 ${drawerOpen ? "translate-x-0" : "-translate-x-full"}`} data-testid="app-sidebar">
        <div className="h-14 flex items-center justify-between px-3 border-b border-[#1E293B]">
          <Link to="/app" className="flex items-center gap-2">
            {collapsed ? <FexidocMark size={26}/> : <FexidocLogo />}
          </Link>
          <button onClick={() => setCollapsed(!collapsed)} className="text-slate-400 hover:text-white p-1 hidden lg:block" data-testid="sidebar-toggle">
            {collapsed ? <PanelLeft size={16}/> : <PanelLeftClose size={16}/>}
          </button>
          <button onClick={() => setDrawerOpen(false)} className="text-slate-400 hover:text-white p-1 lg:hidden" data-testid="sidebar-close">
            <X size={18}/>
          </button>
        </div>
        {/* Workspace switcher */}
        <div className="p-3 relative">
          <button onClick={() => setWsOpen(!wsOpen)} className="w-full flex items-center gap-2 px-2 py-2 rounded-lg bg-[#0D1224] border border-[#1E293B] hover:border-slate-600 transition text-left" data-testid="workspace-switcher">
            <div className="w-7 h-7 rounded-md bg-gradient-to-br from-cyan-400 via-blue-500 to-violet-500 flex items-center justify-center text-[11px] font-bold text-white">
              {(activeWorkspace?.name || "F").slice(0,1).toUpperCase()}
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-100 truncate">{activeWorkspace?.name || "No workspace"}</div>
                <div className="text-[10px] text-slate-500 uppercase tracking-wider">{activeWorkspace?.industry || "workspace"}</div>
              </div>
            )}
            {!collapsed && <ChevronDown size={14} className="text-slate-400"/>}
          </button>
          {wsOpen && !collapsed && (
            <div className="absolute left-3 right-3 mt-2 fx-panel-2 rounded-lg shadow-xl z-30" data-testid="workspace-menu">
              {workspaces.map((w) => (
                <button key={w.id} onClick={() => { selectWorkspace(w); setWsOpen(false); }} className="w-full text-left px-3 py-2 hover:bg-white/5 flex items-center gap-2 text-sm" data-testid={`workspace-option-${w.id}`}>
                  <span className="w-2 h-2 rounded-full bg-cyan-400" />
                  <span className="truncate">{w.name}</span>
                </button>
              ))}
              <div className="border-t border-[#1E293B]" />
              <Link to="/onboarding" onClick={() => setWsOpen(false)} className="block px-3 py-2 text-sm text-cyan-300 hover:bg-white/5">+ New workspace</Link>
            </div>
          )}
        </div>
        <nav className="flex-1 px-2 space-y-1 fx-scrollbar overflow-y-auto">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testid}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition ${isActive ? "bg-gradient-to-r from-[#1478FF]/20 to-[#725BFF]/10 text-white border border-[#1478FF]/30" : "text-slate-400 hover:text-white hover:bg-white/5"}`}>
              <n.icon size={16} />
              {!collapsed && <span>{n.label}</span>}
            </NavLink>
          ))}
        </nav>
        <div className="p-2 border-t border-[#1E293B] space-y-1">
          {bottomNav.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={n.testid}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition ${isActive ? "bg-white/5 text-white" : "text-slate-400 hover:text-white hover:bg-white/5"}`}>
              <n.icon size={16} />
              {!collapsed && <span>{n.label}</span>}
            </NavLink>
          ))}
        </div>
        <div className="p-3 border-t border-[#1E293B] flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-xs font-medium">
            {(user?.first_name || "?").slice(0,1)}{(user?.last_name || "").slice(0,1)}
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <div className="text-sm text-slate-100 truncate">{user?.first_name} {user?.last_name}</div>
              <div className="text-xs text-slate-500 truncate">{user?.email}</div>
            </div>
          )}
          <button onClick={doLogout} className="text-slate-400 hover:text-white p-1" title="Sign out" data-testid="logout-btn"><LogOut size={16}/></button>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        <div className="h-14 border-b border-[#1E293B] bg-[#070B18] flex items-center gap-3 px-4 lg:hidden sticky top-0 z-20" data-testid="mobile-topbar">
          <button onClick={() => setDrawerOpen(true)} className="text-slate-300 hover:text-white p-1" aria-label="Open navigation" data-testid="sidebar-open">
            <Menu size={20}/>
          </button>
          <Link to="/app"><FexidocLogo size={26}/></Link>
          <span className="ml-auto text-xs text-slate-400 truncate max-w-[45%]">{activeWorkspace?.name}</span>
        </div>
        <VerifyBanner />
        <VerifyGate />
        <Outlet />
      </main>
    </div>
  );
}

function VerifyBanner() {
  const { user, refresh } = useAuth();
  const [sent, setSent] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  if (!user || user.email_verified) return null;
  const resend = async () => {
    setBusy(true);
    try {
      await (await import("@/lib/api")).api.post("/auth/resend-verification");
      setSent(true);
      try { await refresh(); } catch {}
    } finally { setBusy(false); }
  };
  return (
    <div className="bg-amber-500/10 border-b border-amber-500/30 text-amber-200 px-4 py-2 text-sm flex items-center justify-between gap-3" data-testid="verify-banner">
      <div>Please verify <span className="text-amber-100">{user.email}</span> to unlock document generation, invites and billing.</div>
      {sent ? (
        <span className="text-xs text-amber-300">Email sent — check your inbox.</span>
      ) : (
        <button onClick={resend} disabled={busy} className="text-xs border border-amber-500/40 hover:bg-amber-500/10 text-amber-200 px-3 py-1 rounded" data-testid="resend-verify-btn">{busy ? "Sending…" : "Resend email"}</button>
      )}
    </div>
  );
}
